import "./location-search-elem.js";

class HeaderElem extends HTMLElement {
    static get observedAttributes() {
        return ["current-page", "floating", "with-search", "placeholder"];
    }

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
        if(window.location.hash.includes("no_chrome")) {
            document.body.classList.add("no_chrome");
        }
        this.render();
        this.setupEventListeners();
    }

    attributeChangedCallback() {
        if(this.shadowRoot.innerHTML) {
            this.render();
            this.setupEventListeners();
        }
    }

    get currentPage() {
        return this.getAttribute("current-page") || "";
    }

    get floating() {
        return this.hasAttribute("floating");
    }

    get withSearch() {
        return this.hasAttribute("with-search");
    }

    render() {
        const navItems = [
            { href: "/blog", label: "Blog", page: "blog" },
            { href: "/help", label: "Help", page: "help" },
        ];

        const placeholderAttr = this.getAttribute("placeholder");
        const searchHTML = this.withSearch ? `
          <div class="search">
            <location-search-elem${placeholderAttr ? ` placeholder="${placeholderAttr.replace(/"/g, "&quot;")}"` : ""}></location-search-elem>
            <a class="current_location" href="javascript:;" title="Use current location" aria-label="Use current location">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="7"/>
                <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>
                <line x1="12" y1="2" x2="12" y2="5"/>
                <line x1="12" y1="19" x2="12" y2="22"/>
                <line x1="2" y1="12" x2="5" y2="12"/>
                <line x1="19" y1="12" x2="22" y2="12"/>
              </svg>
            </a>
          </div>
        ` : "";

        this.shadowRoot.innerHTML = `
            <style>
              :host {
                display: block;
              }

              :host([floating]) {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
              }

              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }

              .header-container {
                margin: 0 auto;
                padding: 0 10rem;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 3rem;
                height: 10rem;
              }

              .search {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
              }

              .search .current_location {
                color: var(--action-color);
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 3rem;
                height: 3rem;
              }

              .search .current_location svg {
                width: 2.4rem;
                height: 2.4rem;
              }

              nav {
                display: flex;
                align-items: center;
                gap: 3.6rem;
              }

              nav a {
                text-decoration: none;
                color: var(--action-color);
                font-size: 1.75rem;
                font-weight: 500;
                transition: color 0.2s ease;
              }

              .hamburger {
                display: none;
                flex-direction: column;
                justify-content: space-between;
                width: 24px;
                height: 18px;
                background: none;
                border: none;
                cursor: pointer;
                padding: 0;
              }

              .hamburger span {
                display: block;
                width: 100%;
                height: 2px;
                background: #333;
                border-radius: 2px;
                transition: all 0.3s ease;
              }

              .hamburger.active span:nth-child(1) {
                transform: rotate(45deg) translate(5px, 5px);
              }

              .hamburger.active span:nth-child(2) {
                opacity: 0;
              }

              .hamburger.active span:nth-child(3) {
                transform: rotate(-45deg) translate(6px, -6px);
              }

              @media (max-width: 768px), (max-height: 500px) {
                .header-container {
                  margin: 0 auto;
                  padding: 0 3rem;
                  gap: 1.5rem;
                  height: 7rem;
                }

                .logo {
                  width: 40px;
                  height: auto;
                }

                .hamburger {
                  display: flex;
                }

                nav {
                  position: absolute;
                  top: 6rem;
                  left: 0;
                  right: 0;
                  background: var(--shaded-background-color);
                  flex-direction: column;
                  gap: 0;
                  padding: 0;
                  max-height: 0;
                  overflow: hidden;
                  transition: max-height 0.3s ease, box-shadow 0.3s ease;
                  box-shadow: none;
                  z-index: 1000;
                }

                nav.open {
                  max-height: 400px;
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

                nav a {
                  padding: 1.8rem 2.7rem;
                  width: 100%;
                  border-bottom: 1px solid #f0f0f0;
                }

                nav a:hover {
                  background: #f9f9f9;
                }
              }

              /* Floating mobile nav needs solid background */
              @media (max-width: 768px), (max-height: 500px) {
                :host([floating]) nav {
                  background: rgba(40, 40, 40, 0.95);
                  backdrop-filter: blur(8px);
                }

                :host([floating]) nav a {
                  border-bottom-color: rgba(255, 255, 255, 0.1);
                  text-shadow: none;
                }

                :host([floating]) nav a:hover {
                  background: rgba(255, 255, 255, 0.1);
                }
              }
            </style>

            <header>
              <div class="header-container">
                <a href="/"><img class="logo" src="/img/logo.png" width="60px" /></a>
                ${searchHTML}
                <button class="hamburger" aria-label="Toggle navigation" aria-expanded="false">
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
                <nav>
                  ${navItems.map(item => `
                    <a href="${item.href}" class="${this.currentPage === item.page ? "active" : ""}">${item.label}</a>
                  `).join("")}
                </nav>
              </div>
            </header>
        `;

        this.$location_search = this.shadowRoot.querySelector("location-search-elem");
        this.$current_location = this.shadowRoot.querySelector(".current_location");
    }

    setupEventListeners() {
        const hamburger = this.shadowRoot.querySelector(".hamburger");
        const nav = this.shadowRoot.querySelector("nav");

        hamburger.addEventListener("click", () => {
            hamburger.classList.toggle("active");
            nav.classList.toggle("open");
            const isOpen = nav.classList.contains("open");
            hamburger.setAttribute("aria-expanded", isOpen);
        });

        nav.querySelectorAll("a").forEach(link => {
            link.addEventListener("click", () => {
                hamburger.classList.remove("active");
                nav.classList.remove("open");
                hamburger.setAttribute("aria-expanded", "false");
            });
        });

        if(this.$current_location) {
            if(!navigator.geolocation) {
                this.$current_location.style.display = "none";
            } else {
                this.$current_location.addEventListener("click", () => this.load_current_location());
            }
        }
    }

    set_location_name(name) {
        if(this.$location_search) {
            this.$location_search.set_location_name(name);
        }
    }

    load_current_location() {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                this.dispatchEvent(new CustomEvent("latlon_chosen", {
                    bubbles: true,
                    composed: true,
                    detail: {
                        lat: position.coords.latitude,
                        lon: position.coords.longitude,
                    },
                }));
            },
            (err) => {
                console.error("CURRENT LOCATION ERROR", err);
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
            }
        );
    }
}

customElements.define("header-elem", HeaderElem);
